import type {
  Color,
  GameState,
  PieceId,
  PieceState,
  PieceType,
  Square,
} from '@ccc/shared-types';
import { sq } from './board.js';

let pieceIdCounter = 0;
export function nextPieceId(color: Color, type: PieceType, origin: Square): PieceId {
  pieceIdCounter += 1;
  return `${color[0]}-${type[0]}-${origin}-${String(pieceIdCounter).padStart(3, '0')}`;
}

export function resetPieceIdCounter(): void {
  pieceIdCounter = 0;
}

function piece(color: Color, type: PieceType, square: Square): PieceState {
  return {
    id: nextPieceId(color, type, square),
    type,
    color,
    square,
    hasMoved: false,
    customFlags: {},
  };
}

const BACK_RANK: PieceType[] = [
  'rook',
  'knight',
  'bishop',
  'queen',
  'king',
  'bishop',
  'knight',
  'rook',
];

const FILE_NAMES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

export function initialPosition(): Record<PieceId, PieceState> {
  const out: Record<PieceId, PieceState> = {};
  for (let i = 0; i < 8; i++) {
    const file = FILE_NAMES[i]!;
    // pawns
    const wp = piece('white', 'pawn', sq(file, 2));
    const bp = piece('black', 'pawn', sq(file, 7));
    out[wp.id] = wp;
    out[bp.id] = bp;
    // back-rank
    const wPiece = piece('white', BACK_RANK[i]!, sq(file, 1));
    const bPiece = piece('black', BACK_RANK[i]!, sq(file, 8));
    out[wPiece.id] = wPiece;
    out[bPiece.id] = bPiece;
  }
  return out;
}

export interface CreateGameOptions {
  id?: string;
  initialSeconds?: number;
  incrementSeconds?: number;
  rated?: boolean;
  activeRules?: { white: string | null; black: string | null };
}

export function createInitialGameState(opts: CreateGameOptions = {}): GameState {
  return {
    id: opts.id ?? 'game-' + Math.random().toString(36).slice(2, 10),
    pieces: initialPosition(),
    turn: 'white',
    moveNumber: 1,
    history: [],
    actionHistory: [],
    activeRules: opts.activeRules ?? { white: null, black: null },
    ruleState: {},
    clocks: {
      whiteMs: (opts.initialSeconds ?? 600) * 1000,
      blackMs: (opts.initialSeconds ?? 600) * 1000,
      lastTickAt: 0,
      ticking: null,
    },
    status: opts.activeRules ? 'active' : 'awaiting_picks',
    initialSeconds: opts.initialSeconds ?? 600,
    incrementSeconds: opts.incrementSeconds ?? 0,
    rated: opts.rated ?? false,
  };
}

export function pieceAt(state: GameState, square: Square): PieceState | undefined {
  for (const p of Object.values(state.pieces)) {
    if (p.square === square) return p;
  }
  return undefined;
}

export function piecesOf(state: GameState, color: Color): PieceState[] {
  const out: PieceState[] = [];
  for (const p of Object.values(state.pieces)) if (p.color === color) out.push(p);
  return out;
}

export function findKing(state: GameState, color: Color): PieceState | undefined {
  for (const p of Object.values(state.pieces)) {
    if (p.type === 'king' && p.color === color) return p;
  }
  return undefined;
}

export function findQueens(state: GameState, color: Color): PieceState[] {
  return piecesOf(state, color).filter((p) => p.type === 'queen');
}

export function cloneState(state: GameState): GameState {
  return structuredClone(state);
}
